/* ──────────────────────────────────────────────────────────────
   Firmas por día o periodo · resumen (pie) + detalle + visor
   ────────────────────────────────────────────────────────────── */
import { useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Stack,
  TextField,
  Paper,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Tooltip,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer, Legend } from 'recharts';

import { useFirmasPeriodoReport } from '../../../hooks/useReportJob';
import { useFirmasReserva } from '../../../hooks/useFirmasReserva';
import { fetchFirmaBlob, FirmaDet } from '../../../api/reports';

/* Colores por estado (coinciden con el orden del pie):
   Firmada | No firmadas por profesor | No firmadas por alumno | Firmadas por formulario */
const COLORS = ['#1e9e85', '#ff4d4f', '#fae45b', '#9ad8cf'];

/* ───────── Helpers ───────── */
const isStr = (v: unknown): v is string => typeof v === 'string';
const nonEmpty = (v: unknown) => isStr(v) && v.trim().length > 0;

const getId = (f: any) => Number(f?.id ?? f?.firmaId ?? f?.id_firma ?? f?.rf_id);

const getMotivo = (f: any) => (f?.motivo_form ?? f?.motivo ?? '') as string;

const getNombre = (f: any) =>
  (f?.nombre ?? f?.usuario ?? (f?.idUsuario ? `#${f.idUsuario}` : '—')) as string;

const getRol = (f: any) => (f?.rol ?? f?.rol_usuario ?? f?.role ?? '—') as string;

/* ¿el payload trae el blob inline y con bytes? */
const hasInlinePng = (f: any) => Array.isArray(f?.firma_png?.data) && f.firma_png.data.length > 0;

/* ¿el payload indica claramente que NO hay PNG? (buffer vacío) */
const inlinePngIsEmpty = (f: any) =>
  Array.isArray(f?.firma_png?.data) && f.firma_png.data.length === 0;

/* Fecha normalizada para el detalle */
const fmtDate = (v: any): string | undefined => {
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : undefined;
};

const getFechaStr = (r: any): string | undefined =>
  fmtDate(r?.fecha) ??
  fmtDate(r?.dia) ??
  fmtDate(r?.fecha_clase) ??
  fmtDate(r?.fechaClase) ??
  fmtDate(r?.fecha_reserva) ??
  fmtDate(r?.fechaReserva) ??
  fmtDate(r?.fecha_inicio) ??
  fmtDate(r?.inicio) ??
  fmtDate(r?.start) ??
  fmtDate(r?.fecha_hora);

export default function FirmasPage() {
  const { t } = useTranslation<'translation'>();
  /* 1 ▸ rango seleccionado (si hasta = desde ⇒ día) */
  const [desde, setDesde] = useState(dayjs().format('YYYY-MM-DD'));
  const [hasta, setHasta] = useState(dayjs().format('YYYY-MM-DD'));

  /* 2 ▸ worker: resumen + detalle (nuevo endpoint /reports/firmas) */
  const { run, useStatus } = useFirmasPeriodoReport();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatus(jobId);

  const lanzar = async () => {
    const payload = { desde, ...(hasta ? { hasta } : { hasta: desde }) };
    const { jobId } = await run(payload);
    setJobId(jobId);
  };

  /* 3 ▸ visor de firmas / formularios */
  const [openResId, setOpen] = useState<number>(); // reserva id
  const { data: firmas } = useFirmasReserva(openResId); // FirmaDet[] | undefined

  const [imgURLs, setURLs] = useState<Record<number, string>>({});
  const [fetchTried, setFetchTried] = useState<Record<number, true>>({});
  const [loadingIds, setLoadingIds] = useState<Record<number, true>>({});

  // Descarga diferida: intentamos PNG para cada fila una sola vez.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!Array.isArray(firmas)) return;

      const addURLs: Record<number, string> = {};
      const tried: Record<number, true> = {};
      const loading: Record<number, true> = {};

      for (const f of firmas) {
        const id = getId(f);
        if (!id) continue;

        // no repetir si ya hay url o ya probamos
        if (imgURLs[id] || fetchTried[id]) continue;

        try {
          loading[id] = true;
          const blob = await fetchFirmaBlob(id);
          if (cancelled) return;

          if (blob && (blob as any).size > 0) {
            addURLs[id] = URL.createObjectURL(blob);
          }
        } catch {
          // ignoramos, lo marcaremos como intentado igualmente
        } finally {
          tried[id] = true;
        }
      }

      if (!cancelled) {
        if (Object.keys(loading).length) setLoadingIds((prev) => ({ ...prev, ...loading }));
        if (Object.keys(addURLs).length) setURLs((prev) => ({ ...prev, ...addURLs }));
        if (Object.keys(tried).length) setFetchTried((prev) => ({ ...prev, ...tried }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [firmas, imgURLs, fetchTried]);

  // Revocar ObjectURLs al cerrar
  const closeDialog = () => {
    Object.values(imgURLs).forEach((url) => URL.revokeObjectURL(url));
    setURLs({});
    setFetchTried({});
    setLoadingIds({});
    setOpen(undefined);
  };

  /* ─────────────────────────────────────────────
     3.1 ▸ FILTRO POR PROFESOR (opcional)
     ───────────────────────────────────────────── */
  const [profesorFiltro, setProfesorFiltro] = useState<string | null>(null);

  // Opciones de profesores a partir de resumen/detalle
  const profesorOptions = useMemo(() => {
    const fromResumen = (status?.result?.profesores ?? [])
      .map((p: any) => p.profesor)
      .filter(Boolean);
    const fromDetalles = (status?.result?.detalles ?? [])
      .map((r: any) => r.profesor)
      .filter(Boolean);
    const unique = Array.from(new Set<string>([...fromResumen, ...fromDetalles]));
    return unique.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [status]);

  // Detalles (reservas) filtrados por profesor si hay filtro activo
  const detallesFiltrados: any[] = useMemo(() => {
    const all = status?.result?.detalles ?? [];
    if (!profesorFiltro) return all;
    return all.filter((r: any) => r.profesor === profesorFiltro);
  }, [status, profesorFiltro]);

  /* 4 ▸ datos para el pie (respeta filtro de profesor si existe) */
  const pie = useMemo(() => {
    // Si hay filtro, usamos el resumen del profesor seleccionado; si no, los totales globales
    const sel = profesorFiltro
      ? (status?.result?.profesores ?? []).find((p: any) => p.profesor === profesorFiltro)
      : null;

    const counts = sel ?? status?.result?.totales;
    if (!counts) return [];

    return [
      {
        label: t('firmas.pie.firmada', 'Firmada por profesor y alumno'),
        n: counts.firmadas ?? 0,
        color: COLORS[0],
      },
      {
        label: t('firmas.pie.noProf', 'No firmadas por profesor'),
        n: counts.noProf ?? 0,
        color: COLORS[1],
      },
      {
        label: t('firmas.pie.noAlu', 'No firmadas por alumno'),
        n: counts.noAlu ?? 0,
        color: COLORS[2],
      },
      {
        label: t('firmas.pie.form', 'Firmadas por formulario'),
        n: counts.form ?? 0,
        color: COLORS[3],
      },
    ].filter((d) => d.n > 0);
  }, [status, profesorFiltro, t]);

  /* 5 ▸ agrupa detalle por estado (sobre el conjunto filtrado) */
  const grupos: Record<string, any[]> = useMemo(() => {
    const g: Record<string, any[]> = {};
    (detallesFiltrados ?? []).forEach((r: any) => {
      (g[r.estado] ??= []).push(r);
    });
    return g;
  }, [detallesFiltrados]);

  /* 6 ▸ resumen por profesor (global) */
  const resumenProfes: Array<{
    profesorId: number;
    profesor: string;
    firmadas: number;
    noProf: number;
    noAlu: number;
    form: number;
    total: number;
  }> = status?.result?.profesores ?? [];

  return (
    <Stack spacing={3}>
      {/* selector periodo + botón + filtro profesor */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          type="date"
          label={t('firmas.filtros.desde', 'Desde')}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          label={t('firmas.filtros.hasta', 'Hasta')}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
        />
        <Tooltip title={t('firmas.acciones.buscar', 'Buscar')}>
          <IconButton color="primary" onClick={lanzar}>
            <SearchIcon />
          </IconButton>
        </Tooltip>

        {/* FILTRO POR PROFESOR (se muestra cuando hay datos) */}
        {status?.done && (
          <Autocomplete
            sx={{ minWidth: 260 }}
            options={profesorOptions}
            value={profesorFiltro}
            onChange={(_, val) => setProfesorFiltro(val)}
            clearOnEscape
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('firmas.filtros.profesor', 'Profesor')}
                placeholder={t('firmas.filtros.profesorPh', 'Filtrar por profesor') as string}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        )}
      </Stack>

      {/* progreso */}
      {status && !status.done && <CircularProgress />}

      {/* resumen pie */}
      {status?.done && pie.length > 0 && (
        <Paper sx={{ p: 2, maxWidth: 620 }}>
          {' '}
          {/* un pelín más de ancho ayuda */}
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t('firmas.resumen.titulo', 'Resumen de firmas')}
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
              <Pie
                data={pie}
                dataKey="n"
                nameKey="label"
                outerRadius={110}
                innerRadius={65}
                cx="40%" // deja espacio a la derecha para la leyenda
                cy="50%"
                label={({ percent = 0 }) => `${(percent * 100).toFixed(0)}%`} // solo % dentro
                labelLine={false}
                isAnimationActive={false}
              >
                {pie.map((d) => (
                  <Cell key={d.label} fill={d.color} />
                ))}
              </Pie>

              {/* LEYENDA */}
              <Legend
                layout="vertical"
                verticalAlign="middle"
                align="right"
                wrapperStyle={{ paddingLeft: 8 }} // un poco de aire
              />

              <RTooltip />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* resumen por profesor (global) */}
      {status?.done && (resumenProfes?.length ?? 0) > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('firmas.profes.titulo', 'Resumen por profesor')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('firmas.profes.profesor', 'Profesor')}</TableCell>
                <TableCell align="right">
                  {t('firmas.profes.firmadas', 'Firmada por profesor y alumno')}
                </TableCell>
                <TableCell align="right">
                  {t('firmas.profes.noProf', 'No firmadas por profesor')}
                </TableCell>
                <TableCell align="right">
                  {t('firmas.profes.noAlu', 'No firmadas por alumno')}
                </TableCell>
                <TableCell align="right">
                  {t('firmas.profes.form', 'Firmadas por formulario')}
                </TableCell>
                <TableCell align="right">
                  {t('firmas.profes.total', 'Total de clases del periodo')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resumenProfes.map((p) => (
                <TableRow key={p.profesorId}>
                  <TableCell>{p.profesor}</TableCell>
                  <TableCell align="right">{p.firmadas}</TableCell>
                  <TableCell align="right">{p.noProf}</TableCell>
                  <TableCell align="right">{p.noAlu}</TableCell>
                  <TableCell align="right">{p.form}</TableCell>
                  {/* IMPORTANTE: mostramos el total del backend,
                      para no “sumar dos veces” cuando falten ambos. */}
                  <TableCell align="right">{p.total}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* detalle por estado (filtrado) */}
      {Object.entries(grupos).map(([estado, arr]) => (
        <Accordion key={estado} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 500, mr: 1 }}>{estado}</Typography>
            <Typography color="text.secondary">({arr.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('firmas.tabla.fecha', 'Fecha')}</TableCell>
                  <TableCell>{t('firmas.tabla.hora', 'Hora')}</TableCell>
                  <TableCell>{t('firmas.tabla.proyecto', 'Proyecto')}</TableCell>
                  <TableCell>{t('firmas.tabla.asignatura', 'Asignatura')}</TableCell>
                  <TableCell>{t('firmas.tabla.profesor', 'Profesor')}</TableCell>
                  <TableCell>{t('firmas.tabla.alumno', 'Alumno')}</TableCell>
                  <TableCell align="right">{t('firmas.tabla.acciones', 'Acciones')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {arr.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell>{getFechaStr(r) ?? '—'}</TableCell>
                    <TableCell>{r.hora}</TableCell>
                    <TableCell>{r.proyecto ?? '—'}</TableCell>
                    <TableCell>{r.asignatura ?? '—'}</TableCell>
                    <TableCell>{r.profesor}</TableCell>
                    <TableCell>{r.cliente}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('firmas.acciones.verFirmas', 'Ver firmas / formularios')}>
                        <IconButton size="small" onClick={() => setOpen(r.id)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* diálogo visor PNGs / formularios */}
      <Dialog open={!!openResId} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('firmas.dialogo.titulo', 'Firmas y formularios de la reserva {{id}}', {
            id: openResId,
          })}
        </DialogTitle>
        <DialogContent dividers>
          {!firmas && <CircularProgress />}

          {Array.isArray(firmas) && (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('firmas.dialogo.nombre', 'Nombre')}</TableCell>
                    <TableCell>{t('firmas.dialogo.rol', 'Rol')}</TableCell>
                    <TableCell>{t('firmas.dialogo.tipo', 'Tipo')}</TableCell>
                    <TableCell>{t('firmas.dialogo.evidencia', 'Evidencia')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {firmas.map((f: FirmaDet) => {
                    const id = getId(f);
                    const motivo = getMotivo(f);
                    const imgUrl = id ? imgURLs[id] : undefined;

                    const digital = !!imgUrl || hasInlinePng(f);
                    const formulario = !digital && (nonEmpty(motivo) || inlinePngIsEmpty(f));

                    const tipo = digital
                      ? t('firmas.tipo.digital', 'Digital')
                      : formulario
                        ? t('firmas.tipo.form', 'Formulario')
                        : '—';

                    const cargando = !!(id && !imgUrl && !formulario && !fetchTried[id]);

                    return (
                      <TableRow key={id}>
                        <TableCell>{getNombre(f)}</TableCell>
                        <TableCell>{getRol(f)}</TableCell>
                        <TableCell>{tipo}</TableCell>
                        <TableCell>
                          {digital && imgUrl ? (
                            <img
                              src={imgUrl}
                              alt={String(t('firmas.dialogo.alt', 'firma'))}
                              style={{ height: 60 }}
                            />
                          ) : formulario ? (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {nonEmpty(motivo)
                                ? motivo
                                : t('firmas.dialogo.sinMotivo', 'Sin motivo')}
                            </Typography>
                          ) : cargando ? (
                            <CircularProgress size={20} />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary">
                {t(
                  'firmas.dialogo.nota',
                  'Las firmas digitales muestran la imagen (PNG). Los formularios muestran el motivo enviado por el alumno.',
                )}
              </Typography>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
